import { useState, useEffect, useRef } from "react";
import SEO from "../components/SEO";

const colors = {
  black: "#343F86",
  charcoal: "#343F86",
  dark: "#262E63",
  mid: "#5B6072",
  ash: "#8A8F9C",
  silver: "#A7ACBD",
  light: "#CDD1E0",
  pale: "#ECEEF6",
  off: "#ECEEF6",
  white: "#FFFFFF",
};

const categories = [
  {
    num: "01",
    icon: "🏗️",
    title: "Design & Project Management",
    desc: "Full-scope architectural, interior, and consultancy services from concept to commissioning.",
    tags: ["Architecture & Construction","Interior Design","Plug & Play","PMC","Express Renovation","NABH Consultant","Elevation"],
  },
  {
    num: "02",
    icon: "⚡",
    title: "MEP & Technical Systems",
    desc: "Mission-critical mechanical, electrical, plumbing and fire systems for healthcare environments.",
    tags: ["Electrical (LT)","Plumbing","Fire System","Medical Gases","HVAC","UPS","Transformers","STP","RO Plant","Heat Pump","Softener","Water Proofing"],
  },
  {
    num: "03",
    icon: "🔧",
    title: "Civil & Finishing Works",
    desc: "Structural, aesthetic, and surface finishing solutions for a complete built environment.",
    tags: ["Civil Work","False Ceiling","Painting","Granite","Tiles","Vinyl Flooring","VDF Flooring","Carpentry","MS Ramp / Staircase","Catwalk"],
  },
  {
    num: "04",
    icon: "🚪",
    title: "Doors, Windows & Interiors",
    desc: "Precision-installed doors, windows, railings, and interior fitting solutions.",
    tags: ["GI Doors","Flush Doors","WPC Doors","Glass UPVC Windows","Window Rollers","SS Railing","Lift","Signage Boards"],
  },
  {
    num: "05",
    icon: "💻",
    title: "Technology & IT Systems",
    desc: "Integrated smart hospital systems — networking, access control, and patient management.",
    tags: ["CCTV Camera","Network","EPBAX","Access Control (Biometric)","Nurse Calling","Hospital Software","Computers","TV"],
  },
  {
    num: "06",
    icon: "🏥",
    title: "Medical & Specialty Equipment",
    desc: "Specialized medical environment fit-outs, furniture, and clinical-grade infrastructure.",
    tags: ["Medical Furniture","Laminar Theatre","Medical Curtains","Office Furniture"],
  },
];

const allServices = [
  "Architecture & Construction","Interior Design","Plug & Play","Project Management Consultant (PMC)",
  "Express Renovation","NABH Consultant","Medical Furniture","Civil Work","Plumbing","Electrical (LT)",
  "Fire System","Medical Gases","False Ceiling","Painting","GI Doors","Flush Doors","WPC Doors",
  "Glass UPVC Windows","CCTV Camera","Network","EPBAX","Access Control (Biometric)","Granite","Lift",
  "SS Railing","Carpentry","Tiles","Vinyl Flooring","Laminar Theatre","RO Plant","HVAC","Elevation",
  "Nurse Calling System","Transformers","UPS","STP","Medical Curtains","VDF Flooring","Water Proofing",
  "TV","Office Furniture","Computers","Hospital Software","MS Ramp & Staircase","Window Rollers",
  "Catwalk","Heat Pump","Softener","Signage Boards",
];

const marqueeItems = [
  "Architecture","Interior Design","NABH","HVAC","Medical Gases","Laminar Theatre",
  "Fire System","Electrical LT","CCTV","Vinyl Flooring","Plug & Play","PMC","RO Plant","Nurse Calling","UPS",
];

const whyPoints = [
  { icon: "🏆", title: "NABH Expertise", text: "Consultants guide you through every step of accreditation compliance." },
  { icon: "⚡", title: "Express Turnaround", text: "Renovation timelines designed for operational hospitals." },
  { icon: "🔩", title: "Single Vendor", text: "50+ services, one contract, zero coordination headaches." },
  { icon: "📐", title: "Plug & Play Ready", text: "Fully commissioned handover every time, on schedule." },
];

const whyCerts = [
  { n: "01", title: "Design to Delivery", desc: "Architecture, interior, MEP, IT — complete project lifecycle managed in-house, reducing risk and cost." },
  { n: "02", title: "Clinical Compliance First", desc: "Every material, layout, and system designed to meet hospital infection control and safety standards." },
  { n: "03", title: "On-Time, On-Budget", desc: "Dedicated project managers ensure every milestone is hit — from foundation to final inspection." },
];

/* ── Styles ──
   Font family: Poppins everywhere (matches global index.css)
   Font scale:  Hero H1 56/32 · Section H2 40/26 · Card H3 20/18 · Body 16/15 · Small 13/12 · Eyebrow 11/11
*/
const s = {
  // Global
  page: { fontFamily: "'Inter', 'Montserrat', sans-serif", color: colors.charcoal, background: colors.white, lineHeight: 1.6 },

  // Intro
  intro: { background: colors.black, padding: "80px 72px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 48, flexWrap: "wrap" },
  introMobile: { background: colors.black, padding: "65px 20px", display: "flex", flexDirection: "column", gap: 32 },
  introLeft: { maxWidth: 580 },
  introEye: { fontFamily: "'Montserrat', 'Inter', sans-serif", fontSize: 11, fontWeight: 600, letterSpacing: 3, textTransform: "uppercase", color: colors.ash, marginBottom: 20 },
  introTitle: { fontFamily: "'Montserrat', 'Inter', sans-serif", fontSize: "clamp(32px, 5vw, 56px)", fontWeight: 700, color: colors.white, lineHeight: 1.1, marginBottom: 20 },
  introDesc: { fontFamily: "'Inter', 'Montserrat', sans-serif", fontSize: 16, color: colors.ash, lineHeight: 1.8, fontWeight: 300 },
  introDescMobile: { fontFamily: "'Inter', 'Montserrat', sans-serif", fontSize: 15, color: colors.ash, lineHeight: 1.75, fontWeight: 300 },
  introStats: { display: "flex", flexDirection: "column", gap: 28, borderLeft: `1px solid ${colors.dark}`, paddingLeft: 48, minWidth: 200 },
  introStatsMobile: { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16, borderTop: `1px solid ${colors.dark}`, paddingTop: 24 },
  statNum: { fontFamily: "'Montserrat', 'Inter', sans-serif", fontSize: 44, fontWeight: 700, color: colors.white, lineHeight: 1 },
  statNumMobile: { fontFamily: "'Montserrat', 'Inter', sans-serif", fontSize: 26, fontWeight: 700, color: colors.white, lineHeight: 1 },
  statLabel: { fontFamily: "'Inter', 'Montserrat', sans-serif", fontSize: 13, fontWeight: 500, color: colors.mid, textTransform: "uppercase", letterSpacing: 1.5, marginTop: 4 },
  statLabelMobile: { fontFamily: "'Inter', 'Montserrat', sans-serif", fontSize: 11, fontWeight: 500, color: colors.mid, textTransform: "uppercase", letterSpacing: 1, marginTop: 4, lineHeight: 1.3 },

  // Marquee
  marqueeWrap: { background: colors.black, padding: "18px 0", overflow: "hidden", whiteSpace: "nowrap", borderTop: `1px solid ${colors.dark}`, borderBottom: `1px solid ${colors.dark}` },
  marqueeItem: { fontFamily: "'Inter', 'Montserrat', sans-serif", display: "inline-block", fontSize: 11, fontWeight: 500, letterSpacing: 2, textTransform: "uppercase", color: colors.mid, marginRight: 48 },
  marqueeDot: { display: "inline-block", width: 4, height: 4, background: colors.dark, borderRadius: "50%", verticalAlign: "middle", marginRight: 48 },

  // Shared section
  sectionPad: { padding: "80px 72px" },
  sectionPadMobile: { padding: "48px 20px" },
  eyebrow: { fontFamily: "'Montserrat', 'Inter', sans-serif", fontSize: 11, fontWeight: 600, letterSpacing: 3, textTransform: "uppercase", color: colors.ash, marginBottom: 12 },
  secTitle: { fontFamily: "'Montserrat', 'Inter', sans-serif", fontSize: "clamp(26px, 3.5vw, 40px)", fontWeight: 700, color: colors.black, lineHeight: 1.15, marginBottom: 10 },
  secSub: { fontFamily: "'Inter', 'Montserrat', sans-serif", fontSize: 16, color: colors.ash, fontWeight: 300, lineHeight: 1.75, maxWidth: 480 },
  secSubMobile: { fontFamily: "'Inter', 'Montserrat', sans-serif", fontSize: 15, color: colors.ash, fontWeight: 300, lineHeight: 1.7, maxWidth: "100%" },

  // Categories
  catSection: { background: colors.off },
  catHeader: { textAlign: "center", marginBottom: 56 },
  catHeaderMobile: { textAlign: "center", marginBottom: 32 },
  catGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 2, background: colors.light, border: `1px solid ${colors.light}`, maxWidth: 1200, margin: "0 auto" },
  catGridMobile: { display: "grid", gridTemplateColumns: "1fr", gap: 2, background: colors.light, border: `1px solid ${colors.light}` },
  catCard: (hovered) => ({ background: hovered ? colors.pale : colors.white, padding: "36px 32px", transition: "background 0.18s", cursor: "default" }),
  catCardMobile: () => ({ background: colors.white, padding: "26px 22px" }),
  catNumber: { fontFamily: "'Montserrat', 'Inter', sans-serif", fontSize: 52, fontWeight: 700, color: colors.pale, lineHeight: 1, marginBottom: 16, userSelect: "none" },
  catNumberMobile: { fontFamily: "'Montserrat', 'Inter', sans-serif", fontSize: 40, fontWeight: 700, color: colors.pale, lineHeight: 1, marginBottom: 12, userSelect: "none" },
  catIconRow: { display: "flex", alignItems: "center", gap: 10, marginBottom: 8 },
  catIconBox: { width: 34, height: 34, background: colors.black, borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, flexShrink: 0 },
  catTitle: { fontFamily: "'Montserrat', 'Inter', sans-serif", fontSize: 20, fontWeight: 600, color: colors.black },
  catTitleMobile: { fontFamily: "'Montserrat', 'Inter', sans-serif", fontSize: 18, fontWeight: 600, color: colors.black },
  catDesc: { fontFamily: "'Inter', 'Montserrat', sans-serif", fontSize: 13, color: colors.ash, lineHeight: 1.65, marginBottom: 20, fontWeight: 300 },
  catTags: { display: "flex", flexWrap: "wrap", gap: 6 },
  catTag: { fontFamily: "'Inter', 'Montserrat', sans-serif", fontSize: 13, fontWeight: 500, color: colors.mid, background: colors.pale, border: `1px solid ${colors.light}`, padding: "4px 10px", borderRadius: 3 },

  // All Services
  svcSection: { background: colors.white },
  svcHeader: { display: "flex", alignItems: "flex-end", justifyContent: "space-between", marginBottom: 48, gap: 24, flexWrap: "wrap" },
  svcHeaderMobile: { display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 12, marginBottom: 28 },
  svcGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 1, background: colors.light, border: `1px solid ${colors.light}`, maxWidth: 1200, margin: "0 auto" },
  svcGridMobile: { display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 1, background: colors.light, border: `1px solid ${colors.light}` },
  svcItem: (hovered) => ({ background: hovered ? colors.black : colors.white, padding: "18px 20px", display: "flex", alignItems: "center", gap: 12, transition: "background 0.15s", cursor: "default" }),
  svcItemMobile: () => ({ background: colors.white, padding: "14px 14px", display: "flex", alignItems: "center", gap: 10 }),
  svcLine: (hovered) => ({ width: 18, height: 1, background: hovered ? colors.ash : colors.light, flexShrink: 0, transition: "background 0.15s" }),
  svcText: (hovered) => ({ fontFamily: "'Inter', 'Montserrat', sans-serif", fontSize: 13, fontWeight: 400, color: hovered ? colors.white : colors.charcoal, lineHeight: 1.35, transition: "color 0.15s" }),
  svcTextMobile: () => ({ fontFamily: "'Inter', 'Montserrat', sans-serif", fontSize: 12, fontWeight: 400, color: colors.charcoal, lineHeight: 1.35 }),

  // Why section
  whySec: { background: colors.charcoal },
  whyInner: { maxWidth: 1100, margin: "0 auto", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 80, alignItems: "start" },
  whyInnerMobile: { display: "flex", flexDirection: "column", gap: 40 },
  whyEye: { fontFamily: "'Montserrat', 'Inter', sans-serif", fontSize: 11, fontWeight: 600, letterSpacing: 3, textTransform: "uppercase", color: colors.silver, marginBottom: 12 },
  whyTitle: { fontFamily: "'Montserrat', 'Inter', sans-serif", fontSize: "clamp(26px, 3.5vw, 40px)", fontWeight: 700, color: colors.white, lineHeight: 1.15, marginBottom: 10 },
  whySub: { fontFamily: "'Inter', 'Montserrat', sans-serif", fontSize: 16, color: colors.ash, fontWeight: 300, lineHeight: 1.75 },
  whySubMobile: { fontFamily: "'Inter', 'Montserrat', sans-serif", fontSize: 15, color: colors.ash, fontWeight: 300, lineHeight: 1.7 },
  whyPoints: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginTop: 40 },
  whyPointsMobile: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 28 },
  whyPt: { border: `1px solid ${colors.dark}`, padding: "22px 18px", borderRadius: 2 },
  whyPtMobile: { border: `1px solid ${colors.dark}`, padding: "16px 14px", borderRadius: 2 },
  whyPtIcon: { fontSize: 20, marginBottom: 10 },
  whyPtTitle: { fontFamily: "'Montserrat', 'Inter', sans-serif", fontSize: 15, fontWeight: 600, color: colors.white, marginBottom: 5 },
  whyPtText: { fontFamily: "'Inter', 'Montserrat', sans-serif", fontSize: 13, color: colors.mid, lineHeight: 1.6, fontWeight: 300 },
  whyRight: { display: "flex", flexDirection: "column", gap: 2 },
  certRow: { border: `1px solid ${colors.dark}`, padding: "28px 24px", display: "flex", alignItems: "flex-start", gap: 20 },
  certRowMobile: { border: `1px solid ${colors.dark}`, padding: "20px 16px", display: "flex", alignItems: "flex-start", gap: 14 },
  certN: { fontFamily: "'Montserrat', 'Inter', sans-serif", fontSize: 36, fontWeight: 700, color: colors.dark, lineHeight: 1, flexShrink: 0, minWidth: 40 },
  certNMobile: { fontFamily: "'Montserrat', 'Inter', sans-serif", fontSize: 26, fontWeight: 700, color: colors.dark, lineHeight: 1, flexShrink: 0, minWidth: 32 },
  certTitle: { fontFamily: "'Montserrat', 'Inter', sans-serif", fontSize: 15, fontWeight: 600, color: colors.white, marginBottom: 5 },
  certDesc: { fontFamily: "'Inter', 'Montserrat', sans-serif", fontSize: 13, color: colors.ash, lineHeight: 1.6, fontWeight: 300 },
};

/* ── Marquee ── */
function Marquee() {
  const doubled = [...marqueeItems, ...marqueeItems];
  return (
    <div style={s.marqueeWrap}>
      <style>{`
        @keyframes marquee { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
        .mq-inner { display: inline-block; animation: marquee 28s linear infinite; }
      `}</style>
      <div className="mq-inner">
        {doubled.map((item, i) => (
          <span key={i}>
            <span style={s.marqueeItem}>{item}</span>
            <span style={s.marqueeDot} />
          </span>
        ))}
      </div>
    </div>
  );
}

/* ── Category Card ── */
function CatCard({ cat, isMobile }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      style={isMobile ? s.catCardMobile() : s.catCard(hovered)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div style={isMobile ? s.catNumberMobile : s.catNumber}>{cat.num}</div>
      <div style={s.catIconRow}>
        <div style={s.catIconBox}>{cat.icon}</div>
        <div style={isMobile ? s.catTitleMobile : s.catTitle}>{cat.title}</div>
      </div>
      <p style={s.catDesc}>{cat.desc}</p>
      <div style={s.catTags}>
        {cat.tags.map((t) => (
          <span key={t} style={s.catTag}>{t}</span>
        ))}
      </div>
    </div>
  );
}

/* ── Service Item ── */
function SvcItem({ name, isMobile }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      style={isMobile ? s.svcItemMobile() : s.svcItem(hovered)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {!isMobile && <div style={s.svcLine(hovered)} />}
      <span style={isMobile ? s.svcTextMobile() : s.svcText(hovered)}>{name}</span>
    </div>
  );
}

/* ── Counter animation ── */
function useCountUp(target, duration = 1200) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    if (typeof target !== "number") { setVal(target); return; }
    const start = performance.now();
    const tick = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      setVal(Math.floor(progress * target));
      if (progress < 1) ref.current = requestAnimationFrame(tick);
    };
    ref.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(ref.current);
  }, [target, duration]);
  return val;
}

function StatItem({ num, label, isMobile }) {
  const isNum = typeof num === "number";
  const count = useCountUp(isNum ? num : 0);
  return (
    <div>
      <div style={isMobile ? s.statNumMobile : s.statNum}>{isNum ? `${count}+` : num}</div>
      <div style={isMobile ? s.statLabelMobile : s.statLabel}>{label}</div>
    </div>
  );
}

/* ── Main Page ── */
export default function HospitalServices() {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  return (
     <>
    <SEO/>
    <div style={s.page}>
      {/* INTRO */}
      <div style={isMobile ? s.introMobile : s.intro}>
        <div style={s.introLeft}>
          <div style={s.introEye}>Hospital Infrastructure · End-to-End Services</div>
          <h1 style={s.introTitle}>Complete Hospital<br />Infrastructure Solutions</h1>
          <p style={isMobile ? s.introDescMobile : s.introDesc}>
            From architectural design and NABH compliance to MEP systems, IT, and plug-and-play commissioning — every discipline delivered under one accountable team.
          </p>
        </div>
        <div style={isMobile ? s.introStatsMobile : s.introStats}>
          <StatItem num={50} label="Services Offered" isMobile={isMobile} />
          <StatItem num={200} label="Projects Delivered" isMobile={isMobile} />
          <StatItem num="NABH" label="Certified Consultants" isMobile={isMobile} />
        </div>
      </div>

      {/* MARQUEE */}
      <Marquee />

      {/* CATEGORIES */}
      <div style={{ ...s.catSection, ...(isMobile ? s.sectionPadMobile : s.sectionPad) }}>
        <div style={isMobile ? s.catHeaderMobile : s.catHeader}>
          <div style={s.eyebrow}>Our Expertise</div>
          <h2 style={s.secTitle}>Service Categories</h2>
          <p style={{ ...(isMobile ? s.secSubMobile : s.secSub), margin: "0 auto" }}>Every discipline your hospital project needs — in one contract, one team.</p>
        </div>
        <div style={isMobile ? s.catGridMobile : s.catGrid}>
          {categories.map((cat) => (
            <CatCard key={cat.num} cat={cat} isMobile={isMobile} />
          ))}
        </div>
      </div>

      {/* ALL SERVICES */}
      <div style={{ ...s.svcSection, ...(isMobile ? s.sectionPadMobile : s.sectionPad) }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <div style={isMobile ? s.svcHeaderMobile : s.svcHeader}>
            <div>
              <div style={s.eyebrow}>Complete List</div>
              <h2 style={s.secTitle}>All 50+ Services</h2>
            </div>
            <p style={{ ...(isMobile ? s.secSubMobile : s.secSub), maxWidth: isMobile ? "100%" : 340 }}>Every service we offer — hover to explore.</p>
          </div>
          <div style={isMobile ? s.svcGridMobile : s.svcGrid}>
            {allServices.map((svc) => (
              <SvcItem key={svc} name={svc} isMobile={isMobile} />
            ))}
          </div>
        </div>
      </div>

      {/* WHY US */}
      <div style={{ ...s.whySec, ...(isMobile ? s.sectionPadMobile : s.sectionPad) }}>
        <div style={isMobile ? s.whyInnerMobile : s.whyInner}>
          <div>
            <div style={s.whyEye}>Why Choose Us</div>
            <h2 style={s.whyTitle}>Your Trusted Hospital Infrastructure Partner</h2>
            <p style={isMobile ? s.whySubMobile : s.whySub}>Deep healthcare domain knowledge combined with construction expertise — every detail meets clinical and regulatory standards.</p>
            <div style={isMobile ? s.whyPointsMobile : s.whyPoints}>
              {whyPoints.map((p) => (
                <div key={p.title} style={isMobile ? s.whyPtMobile : s.whyPt}>
                  <div style={s.whyPtIcon}>{p.icon}</div>
                  <div style={s.whyPtTitle}>{p.title}</div>
                  <div style={s.whyPtText}>{p.text}</div>
                </div>
              ))}
            </div>
          </div>
          <div style={s.whyRight}>
            {whyCerts.map((c) => (
              <div key={c.n} style={isMobile ? s.certRowMobile : s.certRow}>
                <div style={isMobile ? s.certNMobile : s.certN}>{c.n}</div>
                <div>
                  <div style={s.certTitle}>{c.title}</div>
                  <div style={s.certDesc}>{c.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
    </>
  );
}